import { parseHash, serializeHash } from '@/hooks/use-url-hash';

/**
 * Deep links INTO the results panel.
 *
 * The container-results sheet lists a container's pages and each page's
 * extracted fields, but every affordance on it pointed at
 * `/dashboard/ocr-containers/<id>` — the panel's own default view. Opening a
 * page-3 field meant landing on page 1 and hunting for it again.
 *
 * Two targets, both carried in the fragment (the project's convention for
 * in-page state, and the same space the listing's `#results/<id>` sheet uses):
 *
 * - `#page/<globalPageNumber>` — show that page.
 * - `#field/<resultId>/<globalPageNumber>/<fieldKey>` — show that field,
 *   expanded and active, with the document viewer scrolled to its highlight.
 *
 * The page is part of a field's identity, not decoration. A repeating template
 * extracts the same key once PER PAGE — `تاريخ_الفاتورة` exists on all three
 * pages of an invoice — so a link carrying only the key resolved to whichever
 * copy came first and every page's row pointed at page 1's field. `0` means the
 * field belongs to no page (the panel's unattributed section).
 *
 * `fieldKey` is percent-encoded by `serializeHash` and decoded by `parseHash`,
 * which matters: a free-form key is the on-page label, so it is routinely
 * Arabic and contains characters a raw fragment cannot hold.
 *
 * The field target deliberately carries no chunk id. A chunk id is not stable
 * across re-runs (`chunk_001` restarts per synthesis call), so a link holding
 * one would silently point at a different region after the next OCR run. The
 * panel resolves the field's current first reference instead.
 */

export const RESULTS_PAGE_HASH_KEY = 'page';
export const RESULTS_FIELD_HASH_KEY = 'field';

export type ResultsDeepLink =
  | { kind: 'page'; pageNumber: number }
  | {
      kind: 'field';
      resultId: number;
      /** Global 1-based page, or 0 for a field attributed to no page. */
      pageNumber: number;
      fieldKey: string;
    };

/** `#page/3` for the page whose GLOBAL page number is 3. */
export function resultsPageHash(pageNumber: number): string {
  return serializeHash(RESULTS_PAGE_HASH_KEY, [String(pageNumber)]);
}

/**
 * `#field/12/2/CN_CODE` — the run that read the field, the GLOBAL page it sits
 * on (0 = unattributed), and its key.
 */
export function resultsFieldHash(
  resultId: number,
  pageNumber: number,
  fieldKey: string
): string {
  return serializeHash(RESULTS_FIELD_HASH_KEY, [
    String(resultId),
    String(pageNumber),
    fieldKey
  ]);
}

/**
 * The target a fragment names, or null when it names none of ours — the panel
 * shares the fragment with every other hash consumer on the page, so an
 * unrelated key must read as "no target", never as a malformed one.
 */
export function parseResultsDeepLink(hash: string): ResultsDeepLink | null {
  const parsed = parseHash(hash);
  if (!parsed) return null;

  if (parsed.key === RESULTS_PAGE_HASH_KEY) {
    const pageNumber = Number(parsed.params[0]);
    // A page number is 1-based; 0, NaN and negatives are not pages.
    if (!Number.isInteger(pageNumber) || pageNumber < 1) return null;
    return { kind: 'page', pageNumber };
  }

  if (parsed.key === RESULTS_FIELD_HASH_KEY) {
    const resultId = Number(parsed.params[0]);
    const pageNumber = Number(parsed.params[1]);
    const fieldKey = parsed.params[2] ?? '';
    if (
      !Number.isInteger(resultId) ||
      resultId < 1 ||
      !Number.isInteger(pageNumber) ||
      pageNumber < 0 ||
      fieldKey === ''
    ) {
      return null;
    }
    return { kind: 'field', resultId, pageNumber, fieldKey };
  }

  return null;
}

/**
 * The same two targets, addressed as the positional params of the IN-SCREEN
 * results overlay (`#results-view/<containerId>[/<target…>]`) rather than as a
 * fragment on `/dashboard/ocr-containers/<id>`.
 *
 * OMA-635 stopped "view results" from leaving the OCR screen. The route split
 * the two halves of a destination between its path (which container) and its
 * fragment (which page or field); an overlay has only the fragment, so both
 * halves ride in one params list. The container id comes first because it is
 * the only mandatory half — a target without a container names nothing.
 *
 * These take and return the segments `useHashParamDialog` speaks: `openWith`
 * percent-encodes each one and `parseHash` decodes them again, so the tail is
 * re-serialized through `serializeHash` here purely to hand
 * `parseResultsDeepLink` the shape it already parses. That keeps ONE parser for
 * a target — the standalone route and the overlay cannot drift apart.
 */
export function resultsViewParams(
  containerId: number,
  target?: ResultsDeepLink | null
): string[] {
  const id = String(containerId);
  if (!target) return [id];

  if (target.kind === 'page') {
    return [id, RESULTS_PAGE_HASH_KEY, String(target.pageNumber)];
  }

  return [
    id,
    RESULTS_FIELD_HASH_KEY,
    String(target.resultId),
    String(target.pageNumber),
    target.fieldKey
  ];
}

export interface ResultsViewTarget {
  containerId: number;
  /** Where to land inside the panel, or null to open it on its default view. */
  deepLink: ResultsDeepLink | null;
}

/**
 * The overlay a params list names, or null when it names no container.
 *
 * A tail that parses as no target is NOT an error: the overlay still opens on
 * the container, just without jumping. A link written before a re-run
 * re-bucketed a field — or by an older build — has to stay a working click,
 * and refusing the whole overlay over a stale tail is how it stops being one.
 */
export function parseResultsViewParams(
  params: readonly string[]
): ResultsViewTarget | null {
  const containerId = Number(params[0]);
  if (!Number.isInteger(containerId) || containerId < 1) return null;

  const tail = params.slice(1);
  if (tail.length === 0) return { containerId, deepLink: null };

  return {
    containerId,
    deepLink: parseResultsDeepLink(serializeHash(tail[0], tail.slice(1)))
  };
}
